import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { fetchTodayDispatches, type DispatchListItem } from '@/features/warehouse/api/dispatchApi'
import { buildHomeActions, countDispatchStatuses } from '@/features/warehouse/homeActions'
import { warehouseTerms } from '@/features/warehouse/warehouseTerms'
import { getWarehouseDevice } from '@/shared/auth/warehouseDeviceStorage'
import { getWarehouseSession } from '@/shared/auth/warehouseSessionStorage'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'

function actionTitle(dispatch: DispatchListItem): string {
  if (dispatch.status === 'draft') return `Lanjutkan draft ${dispatch.code}`
  return `Pengiriman ${dispatch.code} sudah diserahkan`
}

function actionDetail(dispatch: DispatchListItem): string {
  if (dispatch.status === 'draft') return `→ ${dispatch.target_kitchen_name}`
  return `Lihat / serah ulang · ${dispatch.target_kitchen_name}`
}

function actionBadgeClass(status: string): string {
  return status === 'draft' ? 'badge-draft' : 'badge-warning'
}

function actionBadgeLabel(status: string): string {
  if (status === 'draft') return warehouseTerms.home.priority.high
  return warehouseTerms.home.priority.medium
}

/** Beranda gudang — antrian tindakan hari ini + menu utama pengiriman. */
export function WarehouseHomePage() {
  const navigate = useNavigate()
  const device = getWarehouseDevice()
  const session = getWarehouseSession()

  const [items, setItems] = useState<DispatchListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const todayLabel = new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date())

  const greeting = session?.userName
    ? warehouseTerms.home.greeting(session.userName)
    : warehouseTerms.home.greetingFallback

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    setLoadError(false)
    fetchTodayDispatches()
      .then((res) => {
        if (!cancelled) setItems(res)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => reload(), [reload, refreshKey])

  const actions = useMemo(() => buildHomeActions(items), [items])
  const counts = useMemo(() => countDispatchStatuses(items), [items])

  function openAction(dispatch: DispatchListItem) {
    navigate(`/warehouse/dispatches/${dispatch.id}`, { state: { dispatch } })
  }

  return (
    <section className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-headline-lg-mobile text-primary break-words leading-snug">{greeting}</h1>
          <p className="text-body-sm text-on-surface-variant capitalize mt-0.5">
            {warehouseTerms.home.subtitle(device?.warehouseName ?? '—', todayLabel)}
          </p>
        </div>
        <button
          type="button"
          className="btn-outline shrink-0 min-w-[44px] px-3"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={isLoading}
          aria-label={warehouseTerms.home.refreshLabel}
          title={isLoading ? warehouseTerms.home.refreshLoading : warehouseTerms.home.refreshLabel}
        >
          <span className={`material-symbols-outlined ${isLoading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
      </header>

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{warehouseTerms.home.loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {warehouseTerms.home.retry}
          </button>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-body-lg font-bold text-on-surface">{warehouseTerms.home.actionsTitle}</p>
          {!isLoading && actions.length > 0 && (
            <span className="badge-draft">{warehouseTerms.home.actionsCount(actions.length)}</span>
          )}
        </div>
        {isLoading ? (
          <SkeletonList count={2} label={warehouseTerms.home.loadSummary} />
        ) : loadError ? null : actions.length === 0 ? (
          <EmptyState
            compact
            icon="task_alt"
            title={warehouseTerms.home.emptyTitle}
            description={warehouseTerms.home.emptyDescription}
          />
        ) : (
          <ul className="space-y-2">
            {actions.map((action) => (
              <li key={action.id}>
                <button
                  type="button"
                  className="flex w-full items-start gap-3 rounded-lg border border-outline-variant p-3 text-left hover:border-primary transition-colors"
                  onClick={() => openAction(action.dispatch)}
                >
                  <span className="material-symbols-outlined text-primary mt-0.5">local_shipping</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-body-sm font-semibold text-on-surface break-words">
                        {actionTitle(action.dispatch)}
                      </p>
                      <span className={actionBadgeClass(action.dispatch.status)}>
                        {actionBadgeLabel(action.dispatch.status)}
                      </span>
                    </div>
                    <p className="text-body-sm text-on-surface-variant mt-0.5">{actionDetail(action.dispatch)}</p>
                  </div>
                  <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="text-body-lg font-bold text-on-surface mb-3">{warehouseTerms.home.menuTitle}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to="/warehouse/attendance" className="home-tile flex flex-col gap-2">
            <span className="material-symbols-outlined text-primary text-[28px]">how_to_reg</span>
            <p className="text-body-lg font-bold text-on-surface">Absensi gudang</p>
            <p className="text-body-sm text-on-surface-variant">Check-in atau check-out staf dengan selfie dan PIN.</p>
          </Link>

          <Link
            to="/warehouse/dispatches/new"
            className="home-tile flex flex-col gap-2 border-primary"
          >
            <span className="material-symbols-outlined text-primary text-[28px]">add_box</span>
            <p className="text-body-lg font-bold text-on-surface">{warehouseTerms.home.tiles.newDispatchTitle}</p>
            <p className="text-body-sm text-on-surface-variant">{warehouseTerms.home.tiles.newDispatchDescription}</p>
          </Link>

          <Link to="/warehouse/dispatches" className="home-tile flex flex-col gap-2">
            <span className="material-symbols-outlined text-primary text-[28px]">local_shipping</span>
            <p className="text-body-lg font-bold text-on-surface">{warehouseTerms.home.tiles.todayDispatchTitle}</p>
            <p className="text-body-sm text-on-surface-variant">
              {loadError
                ? '—'
                : warehouseTerms.home.tiles.todayDispatchSubtitle(counts.draft, counts.dispatched)}
            </p>
          </Link>
        </div>
      </div>
    </section>
  )
}
